/* -----------------------------------------------------------------------
 * <copyright company="Microsoft Corporation">
 *   Copyright (c) Microsoft Corporation.  All rights reserved.
 * </copyright>
 * ----------------------------------------------------------------------- */

import { useEffect, useState } from "react";
import { ApplicationStatus } from "../components/constants";
import { IReferenceMetaData, IReferenceMetaError } from "../components/util";
import { applicationApi } from "../services/application-apis/application-apis";
import { logException } from "../services/telemetry/telemetryWrapper";

export const useReferenceMetaData = (
  isAuthenticated: boolean,
  appStatus: string
) => {
  const [referenceMetaData, setReferenceMetaData] =
    useState<IReferenceMetaData>({
      status: "",
      statusDate: "",
      candidatePreferredFirstName: "",
      candidateLastName: "",
      referenceFirstName: "",
      referenceLastName: "",
    });

  const [referenceMetaDataError, setReferenceMetaDataError] =
    useState<IReferenceMetaError>({
      status: 200,
    });

  const [referenceLoaded, setReferenceLoaded] = useState("false");

  useEffect(() => {
    if (appStatus !== referenceMetaData.status) {
      setReferenceMetaData({
        ...referenceMetaData,
        status: appStatus,
        statusDate:
          appStatus !== ApplicationStatus.ERROR ? new Date().toJSON() : "",
      });
    }
  }, [appStatus]);

  useEffect(() => {
    async function fetchReferenceMetaData(isAuthenticated: boolean) {
      if (isAuthenticated) {
        await applicationApi
          .getReFerenceMetadata()
          .then((data) => {
            const response = data.data.operationResult.result;
            setReferenceLoaded("true");
            setReferenceMetaData({
              status: response.status,
              statusDate: response.statusDate,
              candidateLastName: response.candidateLastName,
              candidatePreferredFirstName: response.candidatePreferredFirstName,
              referenceFirstName: response.referenceFirstName,
              referenceLastName: response.referenceLastName,
            });
            setReferenceMetaDataError({
              status: 200,
            });
          })
          .catch((error) => {
            setReferenceLoaded("true");
            if (
              error.response &&
              error.response.data &&
              error.response.data.errorInfo &&
              error.response.data.errorInfo.errorCode
            ) {
              setReferenceMetaDataError({
                status: error.response.status,
                statusCode: error.response.data.errorInfo.errorCode,
              });
            } else if (error.response && error.response.data) {
              setReferenceMetaDataError({
                status: error.response.status,
              });
            } else {
              setReferenceMetaDataError({
                status: 500,
              });
              logException(error);
            }
            setReferenceMetaData({
              status: ApplicationStatus.ERROR,
              statusDate: "",
              candidateLastName: "",
              candidatePreferredFirstName: "",
              referenceFirstName: "",
              referenceLastName: "",
            });
          });
      }
    }
    fetchReferenceMetaData(isAuthenticated);
  }, [isAuthenticated]);
  return {
    referenceMetaData: referenceMetaData,
    referenceMetaDataError: referenceMetaDataError,
    referenceMetaLoaded: referenceLoaded,
  };
};
